import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "@/common/components/carousel";
import { KeyValueObj } from "@/common/types";
import { Button } from "@/common/elements";
import { ClipSpinner } from "@/common/components/spinner";
import { PageSkeletonLoader } from "@/common/components/skeleton/page-skeleton";
import { capitalize, pluralize } from "@/common/utils/general";
import { ProductDetailsWrapper } from "./style";

export interface IProductDetailsProps {
  product: KeyValueObj;
  companyId?: string;
  handleEdit?: (product: any) => void;
  handleDelete?: (id?: string) => void;
  deleteLoading?: boolean;
  isAdmin?: boolean;
  loadingData?: boolean;
}

export function ProductDetails({
  product,
  companyId,
  handleDelete,
  handleEdit,
  deleteLoading,
  isAdmin,
  loadingData,
}: IProductDetailsProps) {
  const isProductFetched = typeof product?.id === "string";
  const imageUrls = product?.images?.map((image: KeyValueObj) => ({
    type: "image",
    fileUrl: image.url,
  }));

  // Packaging hierarchy is returned ordered outermost -> base unit. Each layer's
  // containment_quantity says how many of the next (child) layer it holds; the
  // base unit layer holds nothing (containment_quantity === null).
  const packagingLayers: KeyValueObj[] = Array.isArray(product?.packaging_layers)
    ? product.packaging_layers
    : [];
  const baseLayer = packagingLayers[packagingLayers.length - 1];
  const outermostLayer = packagingLayers[0];
  const baseUnitCount = product?.base_unit_count;
  const showPackagingSummary =
    packagingLayers.length >= 2 && typeof baseUnitCount === "number";

  // MED-651: the backend decides whether this product may be edited (a product
  // that has been ordered never can) and reports why. It is the authority — this
  // only stops the user filling in the whole form to be refused on save. Absent
  // on responses that do not ask for it, in which case nothing is disabled.
  const editEligibility: KeyValueObj | undefined = product?.edit_eligibility;
  const editBlocked = editEligibility?.editable === false;
  const editBlockedReason = editEligibility?.blocked_reason;

  const layerValue = (layer: KeyValueObj, index: number) => {
    const quantity = layer?.containment_quantity;
    // Base unit (or any layer with no containment): show just the type.
    if (quantity === null || quantity === undefined)
      return capitalize(layer?.packaging_type);
    const childType = packagingLayers[index + 1]?.packaging_type;
    const contains = pluralize(quantity, capitalize(childType) || "unit");
    return `${capitalize(layer?.packaging_type)} (contains ${contains})`;
  };

  if (loadingData)
    return (
      <ProductDetailsWrapper>
        <PageSkeletonLoader />
      </ProductDetailsWrapper>
    );

  return (
    <ProductDetailsWrapper>
      {isProductFetched && (
        <>
          <div className="product-details">
            <div className="carousel-wrapper">
              <Carousel files={imageUrls} />
            </div>

            <div className="details">
              {isAdmin && (
                <>
                  <div className="top">
                    <h2 className="company">{product?.owner?.name}</h2>

                    <div className="further-details">
                      <div className="item">
                        <h3 className="title">MedEx code</h3>
                        <h3 className="value">{product?.owner?.medex_code}</h3>
                      </div>

                      <div className="item">
                        <h3 className="title">Classification</h3>
                        <h3 className="value">
                          {product?.owner?.classification}
                        </h3>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="top">
                <h2 className="brand">{product?.brand_name}</h2>
                <h3 className="name">{product?.atc_code?.code_title}</h3>
              </div>
              <div className="further-details">
                <div className="item">
                  <h3 className="title">ATC code</h3>
                  <h3 className="value">{product?.atc_code?.readable_name}</h3>
                </div>
                <div className="item">
                  <h3 className="title">Strength</h3>
                  <h3 className="value">
                    {product.strength}
                    {product.strength_unit === "milligrams" ? "mg" : "g"}
                  </h3>
                </div>
                <div className="item">
                  <h3 className="title">Form</h3>
                  <h3 className="value">{product?.form}</h3>
                </div>
                <div className="item">
                  <h3 className="title">Classification</h3>
                  <h3 className="value">{product?.classification}</h3>
                </div>
                <div className="item">
                  <h3 className="title">Regulation</h3>
                  <h3 className="value">{product?.regulation}</h3>
                </div>
                <div className="item">
                  <h3 className="title">Approval Status</h3>
                  <h3 className="value">{product?.approval_status}</h3>
                </div>
                <div className="item">
                  <h3 className="title">Created At</h3>
                  <h3 className="value">
                    {new Date(product?.created_at)?.toDateString()}
                  </h3>
                </div>
                <div className="item">
                  <h3 className="title">Last Updated At</h3>
                  <h3 className="value">
                    {new Date(product?.updated_at)?.toDateString()}
                  </h3>
                </div>
              </div>

              <div className="packaging">
                <h3 className="section-title">Packaging Hierarchy</h3>
                {packagingLayers.length > 0 ? (
                  <>
                    <div className="levels">
                      {packagingLayers.map((layer, index) => (
                        <div className="item" key={layer?.id ?? index}>
                          <h3 className="title">
                            Level {layer?.position ?? index + 1} Packaging
                          </h3>
                          <h3 className="value">{layerValue(layer, index)}</h3>
                        </div>
                      ))}
                    </div>
                    {showPackagingSummary && (
                      <h3 className="summary">
                        Total:{" "}
                        {pluralize(
                          baseUnitCount,
                          capitalize(baseLayer?.packaging_type) || "unit",
                        )}{" "}
                        per {capitalize(outermostLayer?.packaging_type)}
                      </h3>
                    )}
                  </>
                ) : (
                  <h3 className="empty">No packaging information available</h3>
                )}
              </div>
            </div>
          </div>
          {product.approval_status === "queried" && (
            <div className="query-details">
              <h2>Query Remark:</h2>
              <h3>{product?.remark}</h3>
            </div>
          )}

          {product.owner_id === companyId && !isAdmin && (
            <>
              {editBlocked && editBlockedReason && (
                <div className="edit-blocked">
                  <h3>{editBlockedReason}</h3>
                </div>
              )}
              <div className="controls">
                <Button
                  disabled={deleteLoading}
                  onClick={() => handleDelete && handleDelete(product?.id)}
                  secondary
                >
                  {deleteLoading ? <ClipSpinner /> : "Delete"}
                </Button>
                <Button
                  disabled={deleteLoading || editBlocked}
                  onClick={() => handleEdit && handleEdit(product)}
                >
                  Editor
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </ProductDetailsWrapper>
  );
}
